import { useState, useEffect } from "react";
import { api } from "@/App";
import { Plus, Trash2, FileText, ChevronDown, ChevronRight, Settings2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";

const COLORS = [
  { value: "slate", label: "Gris", class: "bg-slate-500" },
  { value: "red", label: "Rouge", class: "bg-red-500" },
  { value: "orange", label: "Orange", class: "bg-orange-500" },
  { value: "amber", label: "Ambre", class: "bg-amber-500" },
  { value: "emerald", label: "Vert", class: "bg-emerald-500" },
  { value: "blue", label: "Bleu", class: "bg-blue-500" },
  { value: "purple", label: "Violet", class: "bg-purple-500" },
];

export default function PrestationsTab() {
  const [prestations, setPrestations] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subPrestDialog, setSubPrestDialog] = useState(null);
  const [expandedPrestation, setExpandedPrestation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newPrestation, setNewPrestation] = useState({
    label: "",
    has_workflow: false,
    color: "slate"
  });

  const [newSubPrestation, setNewSubPrestation] = useState({
    label: "",
    duree_heures: 2
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prestRes, stepsRes] = await Promise.all([
        api.get("/prestations"),
        api.get("/workflow-steps")
      ]);
      const dedupe = (arr) => Array.from(new Map((arr || []).map(o => [o.id, o])).values());
      setPrestations(dedupe(prestRes.data));
      setWorkflowSteps(dedupe(stepsRes.data));
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const getColorClass = (color) => {
    const map = {
      slate: "bg-slate-100 text-slate-700",
      red: "bg-red-100 text-red-700",
      orange: "bg-orange-100 text-orange-700",
      amber: "bg-amber-100 text-amber-700",
      emerald: "bg-emerald-100 text-emerald-700",
      blue: "bg-blue-100 text-blue-700",
      purple: "bg-purple-100 text-purple-700",
    };
    return map[color] || map.slate;
  };

  const handleAddPrestation = async () => {
    if (!newPrestation.label) {
      toast.error("Veuillez entrer un nom");
      return;
    }
    setSaving(true);
    try {
      const name = newPrestation.label.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
      
      await api.post("/prestations", {
        name,
        label: newPrestation.label,
        has_workflow: newPrestation.has_workflow,
        color: newPrestation.color,
        sub_prestations: []
      });
      toast.success("Prestation ajoutée");
      setDialogOpen(false);
      setNewPrestation({ label: "", has_workflow: false, color: "slate" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleDeletePrestation = async (prestId) => {
    try {
      await api.delete(`/prestations/${prestId}`);
      toast.success("Prestation supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleUpdatePrestation = async (prestId, field, value) => {
    try {
      await api.put(`/prestations/${prestId}`, { [field]: value });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleAddSubPrestation = async () => {
    if (!newSubPrestation.label || !subPrestDialog) return;
    setSaving(true);
    try {
      const prestation = prestations.find(p => p.id === subPrestDialog);
      const currentSubs = prestation?.sub_prestations || [];
      const newSub = {
        id: `sub-${Date.now()}`,
        label: newSubPrestation.label,
        duree_heures: newSubPrestation.duree_heures,
        workflow_steps: []
      };
      
      await api.put(`/prestations/${subPrestDialog}`, {
        sub_prestations: [...currentSubs, newSub]
      });
      toast.success("Sous-prestation ajoutée");
      setSubPrestDialog(null);
      setNewSubPrestation({ label: "", duree_heures: 2 });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleDeleteSubPrestation = async (prestId, subId) => {
    try {
      const prestation = prestations.find(p => p.id === prestId);
      const updatedSubs = (prestation?.sub_prestations || []).filter(s => s.id !== subId);
      await api.put(`/prestations/${prestId}`, { sub_prestations: updatedSubs });
      toast.success("Sous-prestation supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleToggleSubStep = async (prestId, subId, stepId) => {
    try {
      const prestation = prestations.find(p => p.id === prestId);
      const updatedSubs = (prestation?.sub_prestations || []).map(s => {
        if (s.id !== subId) return s;
        const current = s.workflow_steps || [];
        const next = current.includes(stepId)
          ? current.filter(x => x !== stepId)
          : [...current, stepId];
        return { ...s, workflow_steps: next };
      });
      await api.put(`/prestations/${prestId}`, { sub_prestations: updatedSubs });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Types de prestations
            </CardTitle>
            <CardDescription>
              Définissez les types d'interventions avec leurs sous-prestations
            </CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {prestations.map((prestation) => (
              <Collapsible
                key={prestation.id}
                open={expandedPrestation === prestation.id}
                onOpenChange={(open) => setExpandedPrestation(open ? prestation.id : null)}
              >
                <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                  {/* Main Prestation Row */}
                  <div className="flex items-center gap-4 p-4">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="shrink-0">
                        {expandedPrestation === prestation.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    
                    <Badge className={`${getColorClass(prestation.color)} min-w-[100px] justify-center`}>
                      {prestation.label}
                    </Badge>
                    
                    <div className="flex-1 flex items-center gap-4">
                      <Select
                        value={prestation.color}
                        onValueChange={(v) => handleUpdatePrestation(prestation.id, "color", v)}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COLORS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded ${color.class}`} />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={prestation.has_workflow}
                          onCheckedChange={(v) => handleUpdatePrestation(prestation.id, "has_workflow", v)}
                        />
                        <Label className="text-sm">Workflow</Label>
                      </div>
                      
                      {/* Configurer les étapes du workflow pour cette prestation */}
                      {prestation.has_workflow && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedPrestation(expandedPrestation === prestation.id ? null : prestation.id)}
                          className="text-xs"
                        >
                          <Settings2 className="h-3 w-3 mr-1" />
                          Étapes ({(prestation.workflow_steps || []).length || "défaut"})
                        </Button>
                      )}

                      <Badge variant="outline" className="text-slate-500">
                        {(prestation.sub_prestations || []).length} sous-prestation(s)
                      </Badge>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeletePrestation(prestation.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Sub-prestations and Workflow Config */}
                  <CollapsibleContent>
                    <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
                      {/* Configuration du Workflow */}
                      {prestation.has_workflow && (
                        <div className="bg-white rounded-lg border border-slate-200 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                              <Settings2 className="h-4 w-4" />
                              Étapes du workflow
                            </p>
                          </div>
                          <p className="text-xs text-slate-500 mb-3">
                            Sélectionnez les étapes pour cette prestation (laissez vide pour le workflow par défaut)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {workflowSteps.map((step) => {
                              const isSelected = (prestation.workflow_steps || []).includes(step.id);
                              return (
                                <button
                                  key={`${prestation.id}-${step.id}`}
                                  type="button"
                                  onClick={() => {
                                    const currentSteps = prestation.workflow_steps || [];
                                    const newSteps = isSelected
                                      ? currentSteps.filter(s => s !== step.id)
                                      : [...currentSteps, step.id];
                                    handleUpdatePrestation(prestation.id, "workflow_steps", newSteps);
                                  }}
                                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                                    isSelected
                                      ? "bg-red-50 border-red-300 text-red-700"
                                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                  }`}
                                >
                                  {step.label}
                                </button>
                              );
                            })}
                          </div>
                          {(prestation.workflow_steps || []).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs text-slate-500">
                                Ordre: {(prestation.workflow_steps || [])
                                  .map(sid => workflowSteps.find(s => s.id === sid)?.label)
                                  .filter(Boolean)
                                  .join(" → ")}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Sous-prestations */}
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-slate-700">Sous-prestations</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSubPrestDialog(prestation.id)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Ajouter
                        </Button>
                      </div>
                      
                      {(prestation.sub_prestations || []).length === 0 ? (
                        <p className="text-sm text-slate-500 italic">
                          Aucune sous-prestation. Cliquez sur "Ajouter" pour en créer.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {(prestation.sub_prestations || []).map((sub) => (
                            <div
                              key={sub.id}
                              className="p-3 bg-white rounded-lg border border-slate-200"
                              data-testid={`sub-prestation-${sub.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-slate-800">{sub.label}</span>
                                  <Badge variant="outline" className="text-xs">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {sub.duree_heures}h
                                  </Badge>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600 hover:bg-red-50 h-8 w-8"
                                  onClick={() => handleDeleteSubPrestation(prestation.id, sub.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              {/* Workflow spécifique de la sous-prestation */}
                              {prestation.has_workflow && (
                                <div className="mt-2 pt-2 border-t border-slate-100">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-xs text-slate-400 mr-1">Workflow :</span>
                                    {workflowSteps.map((step) => {
                                      const isSelected = (sub.workflow_steps || []).includes(step.id);
                                      return (
                                        <button
                                          key={`${sub.id}-${step.id}`}
                                          type="button"
                                          onClick={() => handleToggleSubStep(prestation.id, sub.id, step.id)}
                                          data-testid={`sub-step-toggle-${sub.id}-${step.id}`}
                                          className={`px-2 py-0.5 rounded border text-xs font-medium transition-all ${
                                            isSelected
                                              ? "bg-red-50 border-red-300 text-red-700"
                                              : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                                          }`}
                                        >
                                          {step.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <p className="text-[11px] text-slate-400 mt-1">
                                    {(sub.workflow_steps || []).length === 0
                                      ? "Hérite du workflow de la catégorie"
                                      : `Ordre : ${(sub.workflow_steps || [])
                                          .map(sid => workflowSteps.find(s => s.id === sid)?.label)
                                          .filter(Boolean)
                                          .join(" → ")}`}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Prestation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle prestation</DialogTitle>
            <DialogDescription>Créer un type d'intervention</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de la prestation *</Label>
              <Input
                value={newPrestation.label}
                onChange={(e) => setNewPrestation({ ...newPrestation, label: e.target.value })}
                placeholder="Ex: Climatisation, Électricité..."
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <Select
                value={newPrestation.color}
                onValueChange={(v) => setNewPrestation({ ...newPrestation, color: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${color.class}`} />
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newPrestation.has_workflow}
                onCheckedChange={(v) => setNewPrestation({ ...newPrestation, has_workflow: v })}
              />
              <Label>Utilise le workflow carrosserie</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddPrestation} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sub-Prestation Dialog */}
      <Dialog open={!!subPrestDialog} onOpenChange={() => setSubPrestDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une sous-prestation</DialogTitle>
            <DialogDescription>
              Détaillez les tâches pour cette prestation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={newSubPrestation.label}
                onChange={(e) => setNewSubPrestation({ ...newSubPrestation, label: e.target.value })}
                placeholder="Ex: Entretien 1 filtre, Géométrie..."
              />
            </div>
            <div className="space-y-2">
              <Label>Durée estimée (heures)</Label>
              <Input
                type="number"
                min={0.5}
                step={0.5}
                value={newSubPrestation.duree_heures}
                onChange={(e) => setNewSubPrestation({ ...newSubPrestation, duree_heures: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubPrestDialog(null)}>Annuler</Button>
            <Button onClick={handleAddSubPrestation} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
